import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import "react-phone-input-2/lib/style.css";
// import { FaTrash } from "react-icons/fa";
// import { MdEditSquare } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ConfirmationModal from "../../components/common/confirm/ConfirmationModal";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { MdSearch } from "react-icons/md";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeadTypes } from "../../store/slices/leadTypeSlice";
import { fetchGroups } from "../../store/slices/groupSlice";
import { fetchSources } from "../../store/slices/sourceSlice";
import { fetchStatus } from "../../store/slices/statusSlice";
import { fetchServiceTypes } from "../../store/slices/serviceTypesSlice";
import { fetchLeads, deleteLead, removeLeadsHiddenInCrm } from "../../store/slices/leadsSlice";
import { fetchAllProjects } from "../../store/slices/projectsSlice";
import {
  setGroupLeadReportRedux,
  setFromDateLeadReportRedux,
  setToDateLeadReportRedux,
  setStatusLeadReportRedux,
} from "../../store/slices/filterSlice";
import { setCurrentPageTitle, resetcurrentLeadID } from "../../store/slices/pageSlice";
import FileUploadLeadReport from "./FileUploadLeadReport";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCurrencySymbol } from "../../utils/currencyUtils";
import DateConversion from "../../utils/DateConversion";
import CommaConvert from "../../utils/CommaConvert";
import { SquarePen, Trash2, EllipsisVertical, Copy, ClipboardPaste, X } from "lucide-react";
import { hasViewOrFullAccess, getFeatureAccessLevel } from "../../utils/featureAccessUtils";
import {
  clearCopiedLead,
  copyLeadToStorage,
  hasCopiedLead,
  peekCopiedLead,
  parseCopiedLeadFromClipboardText,
  pasteLeadAsNewLead,
} from "../../utils/copyLeadUtils";
import { toLeadDropdownStatus } from "../../utils/syncLeadProjectBucketStatus";
import {
  hideLeadInCrm,
  hideLeadsInCrm,
} from "../../utils/crmHiddenLeadsUtils";

const getLeadEntityId = (lead) => String(lead?._id || lead?.id || "");

const LeadReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const currency = useCurrencySymbol();
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } = DateConversion();
  const USAComma = CommaConvert();

  useEffect(() => {
    dispatch(setCurrentPageTitle("Lead Report"));
    dispatch(resetcurrentLeadID());
  }, [dispatch]);

  const { leads } = useSelector((state) => state.leads);
  const { projects, allProjects } = useSelector((state) => state.projects);
  const { group } = useSelector((state) => state.group);
  const { source } = useSelector((state) => state.source);
  const { status } = useSelector((state) => state.status);
  const { leadTypesRedux } = useSelector((state) => state.leadTypesRedux);
  const { serviceTypesRedux } = useSelector((state) => state.serviceTypesRedux);
  const { session } = useSelector((state) => state.session);
  const { employees } = useSelector((state) => state.employees);
  const { profiles } = useSelector((state) => state.companyProfile);
  const pkgNormalized = String(profiles?.[0]?.subscriptionpackage || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const isCrmPackage =
    pkgNormalized === "crm" || pkgNormalized.includes("crm");
  const email = session?.email || (typeof localStorage !== "undefined" ? localStorage.getItem("email") : "") || "";
  const currentEmployee = employees?.find((emp) => emp.EmpEmail === email) ?? null;
  const userLevel = session?.auth || (typeof localStorage !== "undefined" ? localStorage.getItem("auth") : "") || "";
  const hasNoFeatureConfig = Array.isArray(currentEmployee?.featureAccess) && currentEmployee.featureAccess.length === 0;
  const canShowLeadReportFilter =
    userLevel === "L0" ||
    hasNoFeatureConfig ||
    !currentEmployee ||
    hasViewOrFullAccess(currentEmployee, "LEAD REPORTS FILTER");

  const leadReportAccessLevel = getFeatureAccessLevel(currentEmployee, "LEAD REPORTS");
  const isViewOnly = !!currentEmployee && currentEmployee?.EmpAuthLevel !== "L0" && !hasNoFeatureConfig && leadReportAccessLevel && String(leadReportAccessLevel).trim().toUpperCase() === "VIEW ONLY";

  const [showModal, setShowModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [canPasteLead, setCanPasteLead] = useState(() => hasCopiedLead());
  const [isPastingLead, setIsPastingLead] = useState(false);
  const [blinkPasteButton, setBlinkPasteButton] = useState(false);
  const [highlightedPasteLeadId, setHighlightedPasteLeadId] = useState(null);
  const [currentCopiedLeadKey, setCurrentCopiedLeadKey] = useState("");
  const [dismissedPasteBannerKey, setDismissedPasteBannerKey] = useState("");
  const pendingPastePlacementRef = useRef(null);
  // Bulk delete: null | "selecting"
  const [bulkDeletePhase, setBulkDeletePhase] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkIdsToDelete, setBulkIdsToDelete] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [completedLeadPopupOpen, setCompletedLeadPopupOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchLeadTypes());
    dispatch(fetchLeads());
    dispatch(fetchGroups());
    dispatch(fetchSources());
    dispatch(fetchStatus());
    dispatch(fetchLeadTypes());
    dispatch(fetchServiceTypes());
    dispatch(fetchAllProjects());
  }, [dispatch]);

  const syncCopiedLeadState = useCallback(() => {
    const copiedLead = peekCopiedLead();
    const hasLead = !!copiedLead?.fields || hasCopiedLead();
    const nextKey = copiedLead
      ? `${copiedLead.sourceLeadId || "lead"}:${copiedLead.copiedAt || ""}`
      : "";

    setCanPasteLead(hasLead);
    setCurrentCopiedLeadKey((prevKey) => {
      if (nextKey && prevKey !== nextKey) {
        setDismissedPasteBannerKey("");
      }
      return nextKey;
    });

    if (!hasLead) {
      setDismissedPasteBannerKey("");
    }
  }, []);

  useEffect(() => {
    syncCopiedLeadState();
    window.addEventListener("focus", syncCopiedLeadState);
    return () => window.removeEventListener("focus", syncCopiedLeadState);
  }, [syncCopiedLeadState]);

  useEffect(() => {
    if (!blinkPasteButton) return;
    const timer = setTimeout(() => setBlinkPasteButton(false), 3200);
    return () => clearTimeout(timer);
  }, [blinkPasteButton]);

  const showPasteBanner =
    canPasteLead &&
    !isViewOnly &&
    currentCopiedLeadKey !== dismissedPasteBannerKey;

  const handleClearCopiedLead = () => {
    clearCopiedLead();
    setCanPasteLead(false);
    setCurrentCopiedLeadKey("");
    setDismissedPasteBannerKey("");
    setBlinkPasteButton(false);
  };

  const [LeadReports, setLeadReports] = useState([]);
  const [GroupNames, setGroupNames] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredLeadReports, setFilteredLeadReports] = useState([]);
  const [AssignedGroup, setAssignedGroup] = useState("All");
  const getLeadGroupName = (lead) => {
    if (!lead) return "";
    const direct =
      lead?.groupid?.newGroupName ||
      lead?.groupid?.groupName ||
      lead?.groupid?.GroupName ||
      lead?.groupid?.name;
    if (direct) return direct;
    if (lead?.groupid && typeof lead.groupid === "string") {
      const matchedGroup = (group || []).find(
        (gr) => String(gr?._id || gr?.id) === String(lead.groupid)
      );
      if (matchedGroup) {
        return (
          matchedGroup.newGroupName ||
          matchedGroup.groupName ||
          matchedGroup.GroupName ||
          matchedGroup.name ||
          ""
        );
      }
    }
    return lead?.AssignedGroup || "";
  };

  const dropdownsRef = useRef(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If dropdown is open and click is outside the dropdown AND not on the dropdown icon
      if (openDropdownId && dropdownsRef.current && !dropdownsRef.current.contains(event.target)) {
        // Also check if the click was on the ellipsis icon itself
        const clickedElement = event.target;
        const isEllipsisIcon = clickedElement.closest('.dropdown-toggle-icon');

        if (!isEllipsisIcon) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  useEffect(() => {
    setGroupNames(group);
    setStatuses(status);
  }, [group, source, status, leadTypesRedux, serviceTypesRedux]);

  useEffect(() => {
    if (leads?.length > 0) {
      let sorted = [...leads].sort((a, b) => {
        // First, compare by LeadDate (newest first)
        const leadDateA = new Date(a.LeadDate);
        const leadDateB = new Date(b.LeadDate);

        // If LeadDate is different, sort by LeadDate
        if (leadDateB.getTime() !== leadDateA.getTime()) {
          return leadDateB.getTime() - leadDateA.getTime();
        }

        // If LeadDate is the same, extract and compare only the TIME portion from createdAt
        const createdAtTimeA = new Date(a.createdAt);
        const createdAtTimeB = new Date(b.createdAt);

        // Extract time in milliseconds since midnight
        const timeA = createdAtTimeA.getHours() * 3600000 +
          createdAtTimeA.getMinutes() * 60000 +
          createdAtTimeA.getSeconds() * 1000 +
          createdAtTimeA.getMilliseconds();

        const timeB = createdAtTimeB.getHours() * 3600000 +
          createdAtTimeB.getMinutes() * 60000 +
          createdAtTimeB.getSeconds() * 1000 +
          createdAtTimeB.getMilliseconds();

        // Sort by time (latest time first)
        return timeB - timeA;
      });

      // After paste: place the new lead directly below the source lead that was copied
      const placement = pendingPastePlacementRef.current;
      if (placement?.sourceLeadId && placement?.newLeadId) {
        const matchId = (lead, id) =>
          String(lead?._id || lead?.id || "") === String(id);
        const newIdx = sorted.findIndex((l) => matchId(l, placement.newLeadId));
        const srcIdx = sorted.findIndex((l) => matchId(l, placement.sourceLeadId));
        if (newIdx >= 0 && srcIdx >= 0 && newIdx !== srcIdx) {
          const next = [...sorted];
          const [pasted] = next.splice(newIdx, 1);
          const srcAfter = next.findIndex((l) =>
            matchId(l, placement.sourceLeadId)
          );
          if (srcAfter >= 0) {
            next.splice(srcAfter + 1, 0, pasted);
            sorted = next;
          }
        }
        setHighlightedPasteLeadId(placement.newLeadId);
        pendingPastePlacementRef.current = null;
      }

      setLeadReports(sorted);
      setFilteredLeadReports(sorted);
    } else if (Array.isArray(leads)) {
      setLeadReports([]);
      setFilteredLeadReports([]);
    }
  }, [leads]);

  const handleFromDateChange = (date) => {
    setDateRange({ ...dateRange, fromDate: date });
    dispatch(setFromDateLeadReportRedux(date)); //  Dispatch fromDate update
  };

  const handleToDateChange = (date) => {
    setDateRange({ ...dateRange, toDate: date });
    dispatch(setToDateLeadReportRedux(date)); //  Dispatch toDate update
  };

  const handleAssignedGroupChange = (groupName) => {
    setAssignedGroup(groupName); // Update assigned group
    setIsGroupOpen(false); // Close dropdown
    dispatch(setGroupLeadReportRedux(groupName));
  };

  const fromDateLeadReportRedux = useSelector(
    (state) => state.filters.fromDateLeadReport
  );
  const toDateLeadReportRedux = useSelector(
    (state) => state.filters.toDateLeadReport
  );
  const statusLeadReportRedux = useSelector(
    (state) => state.filters.statusLeadReport
  );
  const GroupLeadReportRedux = useSelector(
    (state) => state.filters.GroupLeadReport
  );

  useEffect(() => {
    if (GroupLeadReportRedux) {
      setAssignedGroup(GroupLeadReportRedux);
    }
  }, [GroupLeadReportRedux]);

  useEffect(() => {
    setDateRange((prev) => {
      if (prev.fromDate !== fromDateLeadReportRedux) {
        return { ...prev, fromDate: fromDateLeadReportRedux || "" };
      }
      return prev; // Prevent unnecessary state updates
    });
  }, [fromDateLeadReportRedux]);

  useEffect(() => {
    setDateRange((prev) => {
      if (prev.toDate !== toDateLeadReportRedux) {
        return { ...prev, toDate: toDateLeadReportRedux || "" };
      }
      return prev; // Prevent unnecessary state updates
    });
  }, [toDateLeadReportRedux]);

  const dropdownRef = useRef(null);
  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const groupdownRef = useRef(null);

  const handleClickOutsidegroup = (e) => {
    if (groupdownRef.current && !groupdownRef.current.contains(e.target)) {
      setIsGroupOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutsidegroup);
    return () => {
      document.removeEventListener("click", handleClickOutsidegroup);
    };
  }, []);

  const [, setTodaysDate] = useState("");

  const [showLogout, setshowlogout] = useState(false);
  const popupRef = useRef(null);

  const handleClickOutsides = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setshowlogout(false); // Close the popup if clicked outside
    }
  };
  useEffect(() => {
    if (showLogout) {
      document.addEventListener("mousedown", handleClickOutsides);
    } else {
      document.removeEventListener("mousedown", handleClickOutsides);
    }
    return () => document.removeEventListener("mousedown", handleClickOutsides);
  }, [showLogout]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setTodaysDate(today);
  }, []);

  const ResetSearch = (e) => {
    e.preventDefault();
    handleFromDateChange("");
    setDateRange("");
    setSelectedStatuses([]);
    setAssignedGroup("All");
    dispatch(setFromDateLeadReportRedux("")); //  clear fromDate
    dispatch(setToDateLeadReportRedux("")); //  clear toDate
    dispatch(setStatusLeadReportRedux([]));
    dispatch(setGroupLeadReportRedux("All"));
  };

  const findLeadById = (id) =>
    LeadReports.find((l) => getLeadEntityId(l) === String(id)) ||
    filteredLeadReports.find((l) => getLeadEntityId(l) === String(id)) ||
    (leads || []).find((l) => getLeadEntityId(l) === String(id));

  const isLeadCompletedInErp = (lead) => {
    if (!lead) return false;
    const leadSalesStatus = String(lead.SalesPersonStatus || "")
      .toLowerCase()
      .trim();
    if (leadSalesStatus === "project complete") return true;

    const leadQuote =
      lead.quotationId != null ? String(lead.quotationId) : null;
    const leadProject = String(lead.ProjectName || "")
      .trim()
      .toLowerCase();
    const leadClient = String(lead.ClientName || "")
      .trim()
      .toLowerCase();
    return (allProjects?.length ? allProjects : projects || []).some(
      (project) => {
        const sameByQuote =
          leadQuote != null &&
          project.quotationId != null &&
          String(project.quotationId) === leadQuote;
        const sameByName =
          leadProject &&
          String(project.ProjectName || "")
            .trim()
            .toLowerCase() === leadProject &&
          leadClient &&
          String(project.ClientName || "")
            .trim()
            .toLowerCase() === leadClient;
        if (!sameByQuote && !sameByName) return false;
        return (project.Assignedperson || []).some(
          (person) =>
            String(person?.salespersonstatus || "")
              .toLowerCase()
              .trim() === "project complete"
        );
      }
    );
  };

  const removeLeadFromCrmView = (id) => {
    const normalizedId = String(id);
    hideLeadInCrm(id);
    dispatch(removeLeadsHiddenInCrm([id]));
    setLeadReports((prev) =>
      prev.filter((report) => getLeadEntityId(report) !== normalizedId)
    );
    setFilteredLeadReports((prev) =>
      prev.filter((report) => getLeadEntityId(report) !== normalizedId)
    );
  };

  const deleteLeadReport = async (id) => {
    try {
      const lead = findLeadById(id);
      if (isCrmPackage && isLeadCompletedInErp(lead)) {
        removeLeadFromCrmView(id);
        enqueueSnackbar("Lead deleted successfully!", { variant: "success" });
        return;
      }

      const resultAction = await dispatch(deleteLead(id));
      if (deleteLead.fulfilled.match(resultAction)) {
        enqueueSnackbar("Lead report deleted successfully!", {
          variant: "success",
        });
        const normalizedId = String(id);
        setLeadReports((prevReports) =>
          prevReports.filter(
            (report) => getLeadEntityId(report) !== normalizedId
          )
        );
        setFilteredLeadReports((prevReports) =>
          prevReports.filter(
            (report) => getLeadEntityId(report) !== normalizedId
          )
        );
        dispatch(fetchLeads());
        dispatch(fetchAllProjects());
      } else if (
        isCrmPackage &&
        resultAction.payload?.code === "PROJECT_COMPLETED"
      ) {
        removeLeadFromCrmView(id);
        enqueueSnackbar("Lead deleted successfully!", { variant: "success" });
      } else {
        const message =
          resultAction.payload?.message ||
          resultAction.payload?.error ||
          "Failed to delete lead report!";
        enqueueSnackbar(message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Connection Error!", { variant: "error" });
    }
  };

  const isLeadCompletedLocked = (lead) => {
    if (!lead) return false;
    // CRM has only Project Requests Outstanding — allow delete at any stage (incl. Won).
    // ERP: keep locking leads in the SP "Project Completed" basket.
    if (isCrmPackage) return false;
    return isLeadCompletedInErp(lead);
  };

  const exitBulkDeleteMode = () => {
    setBulkDeletePhase(null);
    setSelectedLeadIds([]);
    setBulkIdsToDelete(null);
  };

  const enterBulkSelectMode = () => {
    setSelectedLeadIds([]);
    setBulkDeletePhase("selecting");
  };

  const selectAllDeletableLeads = (list) => {
    const ids = (list || [])
      .filter((lead) => !isLeadCompletedLocked(lead))
      .map((lead) => lead.id)
      .filter((id) => id !== undefined && id !== null);
    // Merge with existing selections so other pages stay selected
    setSelectedLeadIds((prev) => [...new Set([...prev, ...ids])]);
    setBulkDeletePhase("selecting");
  };

  const toggleLeadSelection = (lead) => {
    if (isLeadCompletedLocked(lead)) {
      setCompletedLeadPopupOpen(true);
      return;
    }
    const id = lead.id;
    setSelectedLeadIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const deleteMultipleLeadReports = async (ids) => {
    if (!ids?.length) return;
    setIsBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    const removedFromCrmOnly = [];
    const apiDeletedIds = [];
    try {
      for (const id of ids) {
        const lead = findLeadById(id);
        if (isCrmPackage && isLeadCompletedInErp(lead)) {
          removedFromCrmOnly.push(id);
          successCount += 1;
          continue;
        }
        const resultAction = await dispatch(deleteLead(id));
        if (deleteLead.fulfilled.match(resultAction)) {
          apiDeletedIds.push(id);
          successCount += 1;
        } else if (
          isCrmPackage &&
          resultAction.payload?.code === "PROJECT_COMPLETED"
        ) {
          removedFromCrmOnly.push(id);
          successCount += 1;
        } else {
          failCount += 1;
        }
      }
      if (removedFromCrmOnly.length) {
        hideLeadsInCrm(removedFromCrmOnly);
        dispatch(removeLeadsHiddenInCrm(removedFromCrmOnly));
      }
      const removedIds = [...removedFromCrmOnly, ...apiDeletedIds];
      if (removedIds.length) {
        setLeadReports((prevReports) =>
          prevReports.filter(
            (report) => !removedIds.includes(getLeadEntityId(report))
          )
        );
        setFilteredLeadReports((prevReports) =>
          prevReports.filter(
            (report) => !removedIds.includes(getLeadEntityId(report))
          )
        );
        setSelectedLeadIds([]);
        if (apiDeletedIds.length) {
          dispatch(fetchLeads());
          dispatch(fetchAllProjects());
        }
      }
      if (failCount === 0) {
        enqueueSnackbar(
          `${successCount} lead(s) deleted successfully!`,
          { variant: "success" }
        );
      } else if (successCount === 0) {
        enqueueSnackbar("Failed to delete selected lead(s).", {
          variant: "error",
        });
      } else {
        enqueueSnackbar(
          `${successCount} deleted, ${failCount} failed.`,
          { variant: "warning" }
        );
      }
    } catch (error) {
      console.error("Bulk delete error:", error);
      enqueueSnackbar("Connection Error!", { variant: "error" });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [LeadToDelete, setLeadToDelete] = useState(null);

  const openDeleteModal = (id) => {
    setBulkIdsToDelete(null);
    setLeadToDelete(id);
    setIsModalOpen(true);
  };

  const openBulkDeleteModal = () => {
    if (!selectedLeadIds.length) {
      enqueueSnackbar("Please select at least one lead to delete.", {
        variant: "warning",
      });
      return;
    }
    setLeadToDelete(null);
    setBulkIdsToDelete([...selectedLeadIds]);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setLeadToDelete(null);
    setBulkIdsToDelete(null);
  };

  const confirmDelete = async () => {
    if (bulkIdsToDelete?.length) {
      const ids = [...bulkIdsToDelete];
      closeDeleteModal();
      await deleteMultipleLeadReports(ids);
      exitBulkDeleteMode();
      return;
    }
    if (LeadToDelete) {
      await deleteLeadReport(LeadToDelete); // Call your existing delete function
    }
    closeDeleteModal();
  };

  const customOrder = ["Won", "Won Recurring", "Pending", "Lost"];

  const sortedStatuses = [...statuses].sort((a, b) => {
    const indexA = customOrder.indexOf(a.status);
    const indexB = customOrder.indexOf(b.status);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.status.localeCompare(b.status);
  });

  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    setSelectedStatuses((prevStatuses) => {
      let updatedStatuses;
      if (prevStatuses.includes(value)) {
        updatedStatuses = prevStatuses.filter((status) => status !== value);
      } else {
        updatedStatuses = [...prevStatuses, value];
      }
      dispatch(setStatusLeadReportRedux(updatedStatuses));
      return updatedStatuses;
    });
  };

  useEffect(() => {
    if (statusLeadReportRedux) {
      setSelectedStatuses(statusLeadReportRedux);
    }
  }, [statusLeadReportRedux]);

  const selectedStatusesString = selectedStatuses.join(", ");
  const filteredLeadReport = filteredLeadReports.filter((LeadReport) => {

    if (
      !dateRange.fromDate &&
      !dateRange.toDate && // No date filter
      selectedStatuses.length === 0 && // No status filter
      AssignedGroup === "All" // No group filter
    ) {
      return true; // No filters, so include all data
    }

    const leadDate = new Date(LeadReport.LeadDate);
    const isWithinDateRange =
      (!dateRange.fromDate && !dateRange.toDate) ||
      (dateRange.fromDate &&
        dateRange.toDate &&
        leadDate >= dateRange.fromDate &&
        leadDate <= dateRange.toDate) ||
      (dateRange.fromDate &&
        !dateRange.toDate &&
        leadDate >= dateRange.fromDate) ||
      (!dateRange.fromDate && dateRange.toDate && leadDate <= dateRange.toDate);

    if (!isWithinDateRange) return false;

    const matchesStatus =
      selectedStatuses.length === 0 ||
      selectedStatuses.includes(toLeadDropdownStatus(LeadReport.Status));

    if (!matchesStatus) return false;

    const matchesGroup =
      AssignedGroup === "All" || getLeadGroupName(LeadReport) === AssignedGroup;

    if (!matchesGroup) return false;

    return true;
  });

  const [currentPage, setCurrentPage] = useState(1); // Track the current page

  const [itemsPerPage] = useState(10);

  const totalItems = filteredLeadReport.length;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const totalPages = Math.ceil(filteredLeadReport.length / itemsPerPage);
  const currentData = filteredLeadReport.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // After paste: jump to the page that contains the pasted lead and blink that row
  useEffect(() => {
    if (!highlightedPasteLeadId) return;

    const matchId = (lead, id) =>
      String(lead?._id || lead?.id || "") === String(id);
    const idx = filteredLeadReport.findIndex((l) =>
      matchId(l, highlightedPasteLeadId)
    );
    if (idx >= 0) {
      setCurrentPage(Math.floor(idx / itemsPerPage) + 1);
    }

    const timer = setTimeout(() => setHighlightedPasteLeadId(null), 3000);
    return () => clearTimeout(timer);
    // Only run when a new paste highlight is set (not on every filter recompute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedPasteLeadId]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value; // Remove leading/trailing spaces
    setSearchQuery(query);
    filterLeadReports(query);
    setCurrentPage(1); // ✅ Reset to first page when searching
  };

  const handlePasteLead = useCallback(async () => {
    if (isViewOnly || isPastingLead) return;

    let payload = peekCopiedLead();
    if (!payload) {
      try {
        const text = await navigator.clipboard.readText();
        payload = parseCopiedLeadFromClipboardText(text);
      } catch (_) {
        // clipboard read may be blocked
      }
    }

    if (!payload?.fields) {
      enqueueSnackbar(
        "No copied lead found. Copy a lead from Lead Report first.",
        { variant: "warning" }
      );
      setCanPasteLead(false);
      setCurrentCopiedLeadKey("");
      return;
    }

    setIsPastingLead(true);
    try {
      const name = localStorage.getItem("name") || "";
      const lastName = localStorage.getItem("lastName") || "";
      const ownerEmail =
        localStorage.getItem("email") || email || "";

      const result = await pasteLeadAsNewLead(dispatch, {
        payload,
        leadOwner: `${name} ${lastName}`.trim(),
        leadOwnerEmail: ownerEmail,
        groups: group || [],
      });

      const newLeadId = result?.lead?._id || result?.lead?.id;
      if (result?.sourceLeadId && newLeadId) {
        pendingPastePlacementRef.current = {
          sourceLeadId: result.sourceLeadId,
          newLeadId,
        };
      }

      await dispatch(fetchLeads());
      dispatch(fetchAllProjects());
      setCanPasteLead(false);
      setCurrentCopiedLeadKey("");

      const parts = [];
      if (result.attachmentsExpected > 0) {
        parts.push(
          `${result.attachmentsUploaded}/${result.attachmentsExpected} attachment(s)`
        );
      }
      if (result.quotationsCreated > 0 || result.quotationsFailed > 0) {
        parts.push(
          `${result.quotationsCreated} quotation(s)`
        );
      }
      const detail = parts.length ? ` (${parts.join(", ")} copied)` : "";
      const hasPartialFailure =
        (result.attachmentsExpected > 0 &&
          result.attachmentsUploaded < result.attachmentsExpected) ||
        result.quotationsFailed > 0;

      enqueueSnackbar(
        hasPartialFailure
          ? `Lead pasted${detail}. Some attachments/quotations could not be copied.`
          : `Lead pasted successfully${detail}.`,
        { variant: hasPartialFailure ? "warning" : "success" }
      );
    } catch (err) {
      console.error("Paste lead failed:", err);
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to paste lead. Please try again.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      syncCopiedLeadState();
    } finally {
      setIsPastingLead(false);
    }
  }, [
    dispatch,
    email,
    enqueueSnackbar,
    group,
    isPastingLead,
    isViewOnly,
    syncCopiedLeadState,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isViewOnly || isPastingLead || !canPasteLead) return;
      const isPasteKey =
        (event.ctrlKey || event.metaKey) &&
        String(event.key || "").toLowerCase() === "v";
      if (!isPasteKey) return;

      const target = event.target;
      const tag = String(target?.tagName || "").toLowerCase();
      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        target?.isContentEditable;
      if (isEditable) return;

      event.preventDefault();
      handlePasteLead();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canPasteLead, handlePasteLead, isPastingLead, isViewOnly]);

  // const [searched, setSearched] = useState([])
  console.log("filteredLeadReports search", filteredLeadReports)
  console.log("currentData leadreport", currentData)

  const filterLeadReports = (query) => {

    if (!query) {
      setFilteredLeadReports(LeadReports);
      // setSearched(LeadReports);
      return;
    }

    const lowerQuery = query.replace(/,/g, '').toLowerCase();
    // Fields we want to allow in search
    const allowedKeys = [
      "LeadDate",
      "FollowupDate",
      "LeadType",
      "AssignedGroup",
      "groupid",
      "TypeService",
      "Source",
      "Status",
      "QuotedValue",
      "ProjectName",
      "ClientName",
      "ClientContactName",
      "_id",
      "id",
      "updatedAt",
      "createdAt"
    ];

    const filtered = LeadReports.filter((item) =>
      Object.entries(item).some(([key, value]) => {
        // Skip all keys not in allowedKeys
        if (!allowedKeys.includes(key)) return false;
        if (value === null || value === undefined) return false;

        if (
          key === "FollowupDate" &&
          typeof item.Status === "string" &&
          item.Status.toLowerCase() === "won"
        ) {
          return false;
        }
        
        // Handle date fields
        if (key === "LeadDate" || key === "FollowupDate") {
          const formattedDate = formatDateByCountry(value).toLowerCase();
          return formattedDate.includes(lowerQuery);
        }
        if (Array.isArray(value)) {
          return value
            .map((v) => String(v).toLowerCase())
            .some((v) => v.includes(lowerQuery));
        }
        if (key === "AssignedGroup" || key === "groupid") {
          return getLeadGroupName(item).toLowerCase().includes(lowerQuery);
        }
        return String(value).toLowerCase().includes(lowerQuery);
      })
    );
    setFilteredLeadReports(filtered);
  };


  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    let sorted = [...filteredLeadReports]; // Always sort from the filtered dataset

    const compareStrings = (a, b) => {
      const valA = (a || "").toString().toLowerCase();
      const valB = (b || "").toString().toLowerCase();
      return valA.localeCompare(valB);
    };
    const compareDates = (a, b) => {
      const dateA = a ? new Date(a) : null;
      const dateB = b ? new Date(b) : null;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA - dateB;
    };

    switch (key) {
      case "AssignedGroup-az":
        sorted.sort((a, b) =>
          compareStrings(getLeadGroupName(a), getLeadGroupName(b))
        );
        break;
      case "AssignedGroup-za":
        sorted.sort((a, b) =>
          compareStrings(getLeadGroupName(b), getLeadGroupName(a))
        );
        break;
      case "ProjectName-az":
        sorted.sort((a, b) => compareStrings(a.ProjectName, b.ProjectName));
        break;
      case "ProjectName-za":
        sorted.sort((a, b) => compareStrings(b.ProjectName, a.ProjectName));
        break;
      case "ClientName-az":
        sorted.sort((a, b) => compareStrings(a.ClientName, b.ClientName));
        break;
      case "ClientName-za":
        sorted.sort((a, b) => compareStrings(b.ClientName, a.ClientName));
        break;
      case "LeadType-az":
        sorted.sort((a, b) => compareStrings(a.LeadType, b.LeadType));
        break;
      case "LeadType-za":
        sorted.sort((a, b) => compareStrings(b.LeadType, a.LeadType));
        break;
      case "Status-az":
        sorted.sort((a, b) => compareStrings(a.Status, b.Status));
        break;
      case "Status-za":
        sorted.sort((a, b) => compareStrings(b.Status, a.Status));
        break;
      case "Source-az":
        sorted.sort((a, b) => compareStrings(a.Status, b.Status));
        break;
      case "Source-za":
        sorted.sort((a, b) => compareStrings(b.Status, a.Status));
        break;
      case "FollowupDate-az":
        sorted.sort((a, b) => compareDates(a.FollowupDate, b.FollowupDate));
        break;
      case "FollowupDate-za":
        sorted.sort((a, b) => compareDates(b.FollowupDate, a.FollowupDate));
        break;
      case "LeadDate-az":
        sorted.sort((a, b) => compareDates(a.LeadDate, b.LeadDate));
        break;
      case "LeadDate-za":
        sorted.sort((a, b) => compareDates(b.LeadDate, a.LeadDate));
        break;
      default:
        break;
    }
    setFilteredLeadReports(sorted);
  };


  const numericSort = (order) => {
    const sortedReports = [...filteredLeadReports];
    order === "az"
      ? sortedReports.sort(
        (a, b) => Number(a.QuotedValue) - Number(b.QuotedValue)
      )
      : sortedReports.sort(
        (a, b) => Number(b.QuotedValue) - Number(a.QuotedValue)
      );
    setFilteredLeadReports(sortedReports); // Update the state
  };

  return (
    <div className=" bg-[#f3f8f8]">
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
      {completedLeadPopupOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-500 bg-opacity-50 z-[1400]">
          <div className="bg-white p-6 rounded-md shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-2">Lead already completed</h2>
            <p className="text-sm text-gray-600 mb-4">
              This lead is in the Project Completed basket, so it cannot be
              selected for delete.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCompletedLeadPopupOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="relative  scroll-visible justify-center top-0 pt-2">
        <div className="pb-4">
          <div className="flex gap-1 justify-between bg-white rounded-md p-1">
            {canShowLeadReportFilter && (
              <div className="flex gap-3 p-1">
                <div className="flex gap-2 justify-center align-middle items-center">
                  <label
                    htmlFor="fromDate"
                    className="block text-sm font-semibold text-gray-700 "
                  >
                    From Date
                  </label>
                  <DatePicker
                    selected={dateRange.fromDate}
                    onChange={handleFromDateChange}
                    dateFormat={getDateFormat()}
                    className="w-24 outline-none date-picker-placeholder items-center justify-between border border-[#e0e7ff] rounded-md p-2 text-xs"
                    placeholderText={getDatePlaceholder()}
                    maxDate={new Date()}
                  />
                </div>

                <div className="flex gap-2 justify-center align-middle items-center">
                  <label
                    htmlFor="toDate"
                    className="block text-sm font-semibold text-gray-700 text-nowrap"
                  >
                    To Date
                  </label>
                  <div className="w-full">
                    <DatePicker
                      selected={dateRange.toDate}
                      onChange={handleToDateChange}
                      dateFormat={getDateFormat()}
                      className="w-24 outline-none date-picker-placeholder items-center justify-between border border-[#e0e7ff] rounded-md p-2 text-xs"
                      placeholderText={getDatePlaceholder()}
                      minDate={
                        dateRange.fromDate
                          ? new Date(dateRange.fromDate.getTime() + 86400000)
                          : null
                      }
                      maxDate={new Date()} // Restrict future dates
                    />
                  </div>
                </div>
                <div
                  className="relative flex gap-2 justify-center align-middle items-center"
                  ref={dropdownRef}
                >
                  <label
                    htmlFor="selectedStatus"
                    className="block text-sm font-semibold text-gray-700 "
                  >
                    Status
                  </label>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                    className="w-24 relative outline-none date-picker-placeholder flex text-nowrap overflow-hidden justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                  >
                    <ExpandMoreIcon
                      color="white"
                      className="absolute cursor-pointer right-1 top-1 bg-white"
                    />
                    {selectedStatusesString || "All"}
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 top-full w-full text-xs border border-gray-400 bg-white shadow-md rounded-md z-[100]">
                      {sortedStatuses.map((statuse) => (
                        <div
                          key={statuse.id}
                          className="flex items-center p-2 hover:bg-gray-200 cursor-pointer"
                          onClick={() =>
                            handleCheckboxChange({
                              target: { value: statuse.status },
                            })
                          } //  Select checkbox on div click
                        >
                          <input
                            type="checkbox"
                            id={statuse.id}
                            value={statuse.status}
                            onChange={handleCheckboxChange}
                            checked={selectedStatuses.includes(statuse.status)}
                            className="mr-2 cursor-pointer"
                            onClick={(e) => e.stopPropagation()} //  Prevent double triggering
                          />
                          <label htmlFor={statuse.id} className="cursor-pointer">
                            {statuse.status}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className="relative flex gap-2 justify-center align-middle items-center"
                  ref={groupdownRef}
                >
                  <label
                    htmlFor="selectedStatus"
                    className="block text-sm font-semibold text-gray-700 "
                  >
                    Group
                  </label>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsGroupOpen(!isGroupOpen);
                    }}
                    className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                  >
                    <span className="truncate min-w-0 flex-1 text-left">
                      {AssignedGroup}
                    </span>
                    <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                  </button>

                  {isGroupOpen && (
                    <div className="absolute left-0 top-full w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-[100]">
                      <div
                        className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === "All" ? "bg-gray-300" : ""
                          }`}
                        onClick={() => {
                          handleAssignedGroupChange("All"); // Select All
                        }}
                      >
                        <label>All Groups</label>
                      </div>

                      {GroupNames.map((group) => (
                        <div
                          key={group.id}
                          className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === group.newGroupName
                            ? "bg-gray-300"
                            : ""
                            }`}
                          onClick={() => {
                            handleAssignedGroupChange(group.newGroupName); // Select individual group
                          }}
                        >
                          <label>{group.newGroupName}</label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className=" w-16 flex align-middle justify-center">
                  <label
                    htmlFor="selectedStatus"
                    className="block text-gray-700"
                  ></label>
                  <button
                    type="button"
                    onClick={ResetSearch}
                    className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                  >
                    Reset{" "}
                  </button>
                </div>
                {!isViewOnly && (
                  <div className="relative flex align-middle items-center gap-1">
                    {bulkDeletePhase === null && (
                      <button
                        type="button"
                        onClick={enterBulkSelectMode}
                        className="relative outline-none flex items-center gap-1 border border-red-200 text-red-600 rounded-md px-3 py-2 text-xs whitespace-nowrap hover:bg-red-50"
                        title="Delete leads"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end items-center gap-2">
              <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
                <MdSearch className=" w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search"
                  className="bg-[#e0e7ff] outline-none w-[80%]"
                />
              </div>
            </div>
          </div>

          {showPasteBanner && (
            <div
              className={`mt-2 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 ${
                blinkPasteButton ? "blink-pasted-lead" : ""
              }`}
            >
              <button
                id="lead-report-paste-btn"
                type="button"
                onClick={handlePasteLead}
                disabled={isPastingLead}
                title="Paste the copied lead (Ctrl+V)"
                className="flex items-center gap-1 text-xs font-semibold text-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ClipboardPaste size={14} />
                {isPastingLead ? "Pasting..." : "Paste Lead"}
              </button>
              <button
                type="button"
                onClick={handleClearCopiedLead}
                className="rounded-full p-1 text-gray-500 hover:bg-blue-100 hover:text-gray-700"
                title="Close paste lead"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {bulkDeletePhase === "selecting" && (
            <div className="mt-2 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <button
                type="button"
                onClick={openBulkDeleteModal}
                disabled={selectedLeadIds.length === 0 || isBulkDeleting}
                className="flex items-center gap-1 text-xs font-semibold text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} />
                {isBulkDeleting
                  ? "Deleting..."
                  : `Delete Selected (${selectedLeadIds.length})`}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => selectAllDeletableLeads(currentData)}
                  className="rounded-md border border-[#e0e7ff] bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={exitBulkDeleteMode}
                  disabled={isBulkDeleting}
                  className="rounded-md border border-[#e0e7ff] bg-white px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="min-h-[10rem] max-h-[580px] bg-white rounded-t-md overflow-auto mt-2 text-capitalize border">
            <div className="min-w-[1400px] border-separate border-spacing-0 ">
              <div className="bg-[#e8e8e8] sticky top-0 z-50 rounded-t-md">
                <div className="flex text-capitalize rounded-t-md">
                  {bulkDeletePhase === "selecting" && (
                    <div className="px-1 py-1 text-center flex justify-center align-middle items-center border text-xs font-semibold w-[2.5%]" />
                  )}
                  <div className="px-1 py-1 text-start flex justify-start align-middle items-center border text-xs font-semibold w-[3.5%]">
                    Sr. No
                  </div>
                  <div className="px-2 w-[9%] py-1 text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between gap-x-4 text-capitalize">
                      Lead Date
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("LeadDate", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("LeadDate", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1 w-[9%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between gap-x-1 text-capitalize">
                      Client Name
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("ClientName", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("ClientName", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1 w-[10.5%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between gap-x-4 text-capitalize">
                      Project Name
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("ProjectName", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("ProjectName", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1 w-[9%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between gap-x-4 text-capitalize">
                      Lead Type
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("LeadType", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("LeadType", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1  w-[11%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between text-capitalize">
                      Quoted Value ({currency})
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => numericSort("za")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => numericSort("az")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1 w-[9%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between gap-x-4 text-capitalize">
                      Status
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("Status", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("Status", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1  w-[9%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between text-capitalize">
                      Source
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("Source", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("Source", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1 w-[9%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between text-capitalize">
                      Group
                      <div className="flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("AssignedGroup", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("AssignedGroup", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-1 py-1 w-[10%] text-start border text-xs font-semibold tracking-wider text-capitalize">
                    <div className="flex items-center justify-between text-capitalize">
                      <div className="w-[80%] ">Follow-Up Date</div>
                      <div className="w-[20%] flex flex-col gap-y-1">
                        <div className="cursor-pointer" onClick={() => sortAlphabetically("FollowupDate", "az")}>
                          <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                        </div>
                        <div className="cursor-pointer -mt-2" onClick={() => sortAlphabetically("FollowupDate", "za")}>
                          <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1  w-[6%] flex items-center justify-center align-middle text-start  text-xs font-semibold tracking-wider text-capitalize">
                    Attachments
                  </div>
                  <div className="px-2 py-1 w-[5%] flex items-center justify-center align-middle text-start  text-xs font-semibold text-capitalize tracking-wider text-capitalize">
                    Action
                  </div>
                </div>
              </div>
              <div className="bg-white divide-y divide-gray-200">
                {currentData.length > 0 ? (
                  currentData?.map((LeadReport, index) => {
                    const isPastedHighlight =
                      highlightedPasteLeadId &&
                      (String(LeadReport._id || "") ===
                        String(highlightedPasteLeadId) ||
                        String(LeadReport.id || "") ===
                          String(highlightedPasteLeadId));
                    const isCompletedLocked = isLeadCompletedLocked(LeadReport);
                    const isSelecting = bulkDeletePhase === "selecting";
                    const isSelected = selectedLeadIds.includes(LeadReport.id);
                    return (
                    <div
                      className={`flex justify-center items-center align-middle text-xs border-b !border-gray-200 text-capitalize min-h-10 ${
                        isPastedHighlight ? "blink-pasted-lead" : ""
                      } ${
                        isSelecting && isCompletedLocked
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-70"
                          : ""
                      } ${
                        isSelecting && isSelected && !isCompletedLocked
                          ? "bg-blue-50"
                          : ""
                      }`}
                      key={LeadReport.id}
                      onClick={(e) => {
                        if (!isSelecting || !isCompletedLocked) return;
                        if (
                          e.target.closest(
                            "input, button, .dropdown-toggle-icon, [data-lead-row-action]"
                          )
                        ) {
                          return;
                        }
                        setCompletedLeadPopupOpen(true);
                      }}
                    >
                      {isSelecting && (
                        <div
                          className="px-1 py-1 text-center w-[2.5%] flex justify-center items-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isCompletedLocked}
                            onChange={() => toggleLeadSelection(LeadReport)}
                            className={
                              isCompletedLocked
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }
                          />
                        </div>
                      )}
                      <div className="px-2 py-1 text-start w-[3.5%]">
                        {index + 1 + (currentPage - 1) * 10}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%]">
                        {formatDateByCountry(LeadReport.LeadDate)}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%] text-capitalize capitalize">
                        {LeadReport.ClientName}
                      </div>
                      <div className="px-2 py-1 text-start w-[10.5%] text-capitalize capitalize">
                        {LeadReport.ProjectName}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%] text-capitalize capitalize">
                        {LeadReport.LeadType}
                      </div>
                      <div className="px-2 py-1 text-start w-[11%] break-all text-wrap">
                        {USAComma(LeadReport.QuotedValue)}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%] text-capitalize capitalize">
                        {toLeadDropdownStatus(LeadReport.Status)}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%] text-capitalize capitalize">
                        {LeadReport.Source}
                      </div>
                      <div className="px-2 py-1 text-start w-[9%] text-capitalize capitalize ">
                        {getLeadGroupName(LeadReport)}
                      </div>
                      <div className="px-2 py-1 text-start w-[10%]">
                        {["Won", "Lost"].includes(toLeadDropdownStatus(LeadReport.Status))
                          ? " "
                          : formatDateByCountry(LeadReport.FollowupDate)}
                      </div>
                      <div
                        className="px-1 py-1 h-full relative w-[6%] flex gap-2 justify-center text-centre align-middle items-center"
                        data-lead-row-action="attachments"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AttachFileIcon
                          className="rotate-12 cursor-pointer"
                          onClick={() => {
                            setShowModal(true);
                            setSelectedLeadId(LeadReport.id);
                          }}
                        />
                      </div>
                      <div
                        className={`px-2 py-1 relative w-[5%] text-center ${isViewOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                        data-lead-row-action="menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isViewOnly) return;
                            if (openDropdownId !== LeadReport._id) {
                              setOpenDropdownId(LeadReport._id);
                            } else {
                              setOpenDropdownId(null);
                            }
                          }}
                          className={`p-1 dropdown-toggle-icon ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer"}`}
                        >
                          <EllipsisVertical />
                        </div>
                        {openDropdownId === LeadReport._id && (
                          <div
                            ref={dropdownsRef}
                            className={`z-50 p-1 absolute flex flex-col  ${((index === 0 && currentData?.length === 2) ? "top-full" : (index === 1 && currentData?.length === 2) ? "bottom-full" : currentData?.length === 1 ? "top-full" : (index === currentData?.length - 1 || index === currentData?.length - 2) ? 'bottom-full' : 'top-full')} -left-3 bg-white rounded-md shadow border`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              onClick={() => {
                                setOpenDropdownId(null);
                                navigate(`/dashboard/edit-lead/${LeadReport.id}`, {
                                  state: { from: location.pathname },
                                });
                              }}
                              className="p-1 flex gap-1 w-full justify-start align-middle items-center border-b cursor-pointer hover:bg-gray-50"
                            >
                              <span title="Edit">
                                <SquarePen size={15} className="text-blue-500" />
                              </span>
                              Edit
                            </div>
                            <div
                              onClick={async () => {
                                setOpenDropdownId(null);
                                try {
                                  await copyLeadToStorage(
                                    dispatch,
                                    LeadReport,
                                    group
                                  );
                                  setCanPasteLead(true);
                                  const copiedLead = peekCopiedLead();
                                  setCurrentCopiedLeadKey(
                                    copiedLead
                                      ? `${copiedLead.sourceLeadId || "lead"}:${copiedLead.copiedAt || ""}`
                                      : ""
                                  );
                                  setDismissedPasteBannerKey("");
                                  setBlinkPasteButton(true);
                                  enqueueSnackbar("Lead has been copied.", {
                                    variant: "success",
                                  });
                                } catch (err) {
                                  console.error("Copy lead failed:", err);
                                  enqueueSnackbar(
                                    "Failed to copy lead. Please try again.",
                                    { variant: "error" }
                                  );
                                }
                              }}
                              className="p-1 flex gap-1 w-full justify-start align-middle items-center border-b cursor-pointer hover:bg-gray-50"
                            >
                              <span title="Copy">
                                <Copy size={15} className="text-blue-500" />
                              </span>
                              Copy
                            </div>
                            <div
                              onClick={() => {
                                setOpenDropdownId(null);
                                openDeleteModal(LeadReport.id);
                              }}
                              title="Delete"
                              className="p-1 flex gap-1 w-full justify-start align-middle items-center cursor-pointer hover:bg-gray-50"
                            >
                              <span title="Delete">
                                <Trash2 size={15} className="text-blue-500" />
                              </span>
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div className="flex">
                    <div className="border p-4 text-start text-red-500 w-full">
                      {searchQuery
                        ? `No results found for "${searchQuery}"`
                        : "No results found"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pagination mt-1 flex justify-between items-center bg-white rounded-md p-1">
            <div>
              {totalItems > 0 ? (
                <>
                  Showing {startIdx + 1} to {Math.min(endIdx, totalItems)} of{" "}
                  {totalItems} Entries
                </>
              ) : (
                <>Showing 0 Entries</>
              )}
            </div>
            {totalPages > 1 && (
              <div>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="px-2 py-2 mx-1 border rounded"
                  disabled={currentPage === 1}
                  title="First Page"
                >
                  <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="px-2 py-2 mx-2 border rounded"
                  disabled={currentPage === 1}
                >
                  <ChevronRightIcon className="w-5 h-5 rotate-180" />
                </button>

                {Array.from({ length: 3 }, (_, index) => {
                  const pageNumber = currentPage - 1 + index;
                  if (pageNumber >= 1 && pageNumber <= totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber ? "bg-gray-300" : ""
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="px-2 py-2 mx-2 border rounded"
                  disabled={currentPage === totalPages}
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-2 py-2 mx-1 border rounded"
                  disabled={currentPage === totalPages}
                  title="Last Page"
                >
                  <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {showModal && (
            <FileUploadLeadReport
              LeadReport={currentData.find(lead => lead.id === selectedLeadId)}
              setShowModal={setShowModal}
              isViewOnly={isViewOnly} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadReport;
